import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GAS_FORM_ENDPOINT } from '@/config/api';
import Route from '@/config/routes/types';
import { isEmail } from '@/shared/utils/string';

export const useContactModalData = () => {
  const navigate = useNavigate();

  const setContactModalClosed = useCallback(() => {
    navigate(Route.ROOT);
  }, [navigate]);

  return {
    setModalClosed: setContactModalClosed
  };
};

type FieldErrors = {
  firstName: string;
  lastName: string;
  email: string;
  description: string;
};

const emptyErrors: FieldErrors = { firstName: '', lastName: '', email: '', description: '' };

export const useContactData = () => {
  const { setModalClosed } = useContactModalData();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<FieldErrors>(emptyErrors);
  const [isLoading, setIsLoading] = useState(false);

  const clearFieldError = useCallback(
    (field: keyof FieldErrors) =>
      setError((errors) => (errors[field] ? { ...errors, [field]: '' } : errors)),
    []
  );

  const setContactFirstName = useCallback(
    (value: string) => {
      setFirstName(value);
      clearFieldError('firstName');
    },
    [clearFieldError]
  );

  const setContactLastName = useCallback(
    (value: string) => {
      setLastName(value);
      clearFieldError('lastName');
    },
    [clearFieldError]
  );

  const setContactEmail = useCallback(
    (value: string) => {
      setEmail(value);
      clearFieldError('email');
    },
    [clearFieldError]
  );

  const setContactDescription = useCallback(
    (value: string) => {
      setDescription(value);
      clearFieldError('description');
    },
    [clearFieldError]
  );

  const resetContactForm = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setDescription('');
    setError(emptyErrors);
  }, []);

  const handleContact = useCallback(async () => {
    const errors: FieldErrors = { ...emptyErrors };

    if (!firstName.trim()) errors.firstName = 'Required';
    if (!lastName.trim()) errors.lastName = 'Required';
    if (!isEmail(email)) errors.email = 'Invalid email';
    // description is optional

    if (Object.values(errors).some(Boolean)) {
      setError(errors);
      return;
    }

    if (!GAS_FORM_ENDPOINT) {
      console.error(
        'VITE_GAS_FORM_ENDPOINT is not configured. See docs/google-apps-script-setup.md.'
      );
      toast.error('Form is not configured. Please try again later.');
      return;
    }

    setIsLoading(true);

    try {
      // Apps Script Web Apps don't support CORS preflight. Sending as text/plain
      // avoids preflight; the server still parses JSON from the body.
      // We use mode: 'no-cors' so the request is fire-and-forget.
      await fetch(GAS_FORM_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          description: description.trim(),
          userAgent: navigator.userAgent
        })
      });

      // With no-cors we can't read the response, so we assume success on
      // network OK. Apps Script will still write the row + send the email.
      toast.success('Thanks! We will be in touch shortly.');
      resetContactForm();
      setModalClosed();
    } catch (submitError) {
      console.error('Form submission failed:', submitError);
      toast.error('Failed to send. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [description, email, firstName, lastName, resetContactForm, setModalClosed]);

  return {
    firstName,
    lastName,
    email,
    description,
    error,
    isLoading,

    setFirstName: setContactFirstName,
    setLastName: setContactLastName,
    setEmail: setContactEmail,
    setDescription: setContactDescription,
    contact: handleContact
  };
};
